import React, { useEffect, useMemo, useState } from "react";
import { searchCars } from "../services/cars";
import { createConversation } from "../services/chat";
import { fetchLocations } from "../services/locations";
import "../styles/cars.css";

const buildLocationLabel = (loc) => {
    if (!loc) return "";
    return [loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ");
};

const OperatorCars = ({ onNavigate }) => {
    const [query, setQuery] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");

    const [locationsById, setLocationsById] = useState({});

    const networkId = parseInt(localStorage.getItem("networkId"));

    useEffect(() => {
        const loadLocations = async () => {
            try {
                const locs = await fetchLocations();
                const map = {};
                for (const l of locs || []) {
                    if (l && l.id) map[l.id] = l;
                }
                setLocationsById(map);
            } catch (_) {
                // ignore; page still works without location labels
            }
        };
        loadLocations();
    }, []);

    const grouped = useMemo(() => {
        const map = new Map();
        for (const it of items || []) {
            const key = (it?.car_number || "").trim().toLowerCase();
            if (!key) continue;
            if (!map.has(key)) {
                map.set(key, { car_number: it.car_number, owners: [], locations: [], count: 0 });
            }
            const g = map.get(key);
            g.count += 1;
            if (it.client_name) g.owners.push(it.client_name);
            if (it.location_id) g.locations.push({ location_id: it.location_id, apartment: it.apartment });
        }

        return Array.from(map.values())
            .map((g) => {
                const uniqueOwners = Array.from(new Set(g.owners)).filter(Boolean);
                const uniqueLocs = [];
                const seen = new Set();
                for (const l of g.locations) {
                    const k = `${l.location_id}:${l.apartment || ""}`;
                    if (seen.has(k)) continue;
                    seen.add(k);
                    uniqueLocs.push(l);
                }
                return {
                    car_number: g.car_number,
                    count: g.count,
                    owners: uniqueOwners,
                    locations: uniqueLocs,
                };
            })
            .sort((a, b) => String(a.car_number).localeCompare(String(b.car_number)));
    }, [items]);

    const runSearch = async () => {
        setError("");
        setInfo("");

        const q = (query || "").trim();
        if (!q) {
            setItems([]);
            setInfo("Введіть номер авто або його частину");
            return;
        }
        if (!networkId) {
            setItems([]);
            setInfo("Немає networkId. Неможливо виконати пошук.");
            return;
        }

        setLoading(true);
        try {
            const res = await searchCars(q);
            setItems(Array.isArray(res?.items) ? res.items : []);
            if ((res?.items || []).length === 0) {
                setInfo("Нічого не знайдено");
            }
        } catch (e) {
            setError(e?.message || "Не вдалося виконати пошук");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateChat = async (carNumber) => {
        setError("");
        setInfo("");

        const num = (carNumber || "").trim();
        if (!num) return;
        if (!networkId) {
            setError("Немає networkId. Неможливо створити переписку.");
            return;
        }

        setLoading(true);
        try {
            const conv = await createConversation({
                title: `Авто ${num}`,
                work_mode: "network",
                network_id: networkId,
            });
            setInfo("✅ Переписку створено");
            if (typeof onNavigate === "function") {
                const headId = conv?.head_id || conv?.id;
                if (headId) {
                    localStorage.setItem("pendingChatOpenHeadId_operator", String(headId));
                    localStorage.setItem("pendingChatOpenTitle_operator", String(conv?.title || `Авто ${num}`));
                }
                onNavigate("chat");
            }
        } catch (e) {
            setError(e?.message || "Не вдалося створити переписку");
        } finally {
            setLoading(false);
        }
    };

    const formatLocs = (locs) => {
        if (!locs || locs.length === 0) return "";
        const parts = locs
            .slice(0, 3)
            .map((l) => {
                const label = buildLocationLabel(locationsById[l.location_id]);
                const apt = l.apartment ? `кв. ${l.apartment}` : "";
                return [label, apt].filter(Boolean).join(", ");
            })
            .filter(Boolean);
        const more = locs.length > 3 ? ` +${locs.length - 3}` : "";
        return parts.join("; ") + more;
    };

    return (
        <div className="cars-container operator-page-width-chat">
            <div className="config-header cars-header">
                <div>
                    <h2>🚗 Авто</h2>
                </div>
            </div>

            <div className="operator-toolbar-card cars-search-card">
                <div className="cars-search">
                    <input
                        value={query}
                        placeholder="Напр.: АА1234ВК"
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") runSearch();
                        }}
                    />
                    <div className="cars-actions">
                        <button className="add-btn" onClick={runSearch} disabled={loading}>Знайти</button>
                        <button
                            className="add-btn refresh secondary"
                            onClick={() => {
                                setQuery("");
                                setItems([]);
                                setError("");
                                setInfo("");
                            }}
                            disabled={loading}
                        >
                            Очистити
                        </button>
                    </div>
                </div>
            </div>

            {loading && <div className="cars-note">Завантаження...</div>}
            {error && <div className="cars-error">❌ {error}</div>}
            {info && !error && <div className="cars-note">{info}</div>}

            <div className="cars-list operator-section-stack">
                {grouped.map((g) => (
                    <div key={g.car_number} className="cars-item operator-accordion-item">
                        <div className="cars-item-main">
                            <div className="cars-item-title">{g.car_number}</div>
                            <div className="cars-item-subtitle">
                                {g.owners.length > 0 ? `Мешканці: ${g.owners.slice(0, 3).join(", ")}${g.owners.length > 3 ? " …" : ""}` : ""}
                                {g.locations.length > 0 ? `${g.owners.length > 0 ? " · " : ""}${formatLocs(g.locations)}` : ""}
                                {g.count > 1 ? `${(g.owners.length > 0 || g.locations.length > 0) ? " · " : ""}Записів: ${g.count}` : ""}
                            </div>
                        </div>
                        <div className="cars-item-actions">
                            <button className="add-btn" onClick={() => handleCreateChat(g.car_number)} disabled={loading}>
                                Створити переписку
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OperatorCars;
